/**
 * =============================================================================
 * TOAST PROVIDER
 * =============================================================================
 *
 * React context provider for managing toast notifications.
 * Provides methods to show success, error, and warning toasts from any component.
 *
 * PHASE 1 TASK: P1-7 - Add Toast Notifications for Admin Actions
 *
 * Usage:
 * 1. Wrap your app with <ToastProvider>
 * 2. Use useToast() hook to access toast methods
 *
 * @example
 * ```tsx
 * // In a component
 * function MyComponent() {
 *   const { showSuccess, showError } = useToast();
 *
 *   const handleApprove = async () => {
 *     try {
 *       await approveQuestion(id);
 *       showSuccess('Question approved successfully');
 *     } catch (error) {
 *       showError('Failed to approve question');
 *     }
 *   };
 * }
 * ```
 *
 * =============================================================================
 */

import React, { createContext, useCallback, useState, useMemo } from 'react';
import type { ReactNode } from 'react';
import { ToastContainer } from '../components/Toast';
import type { ToastAnnouncement, ToastData, ToastVariant } from '../components/Toast';

/**
 * Counter for generating unique toast IDs.
 */
let toastIdCounter = 0;

/**
 * Generate a unique toast ID.
 */
function generateToastId(): string {
  toastIdCounter += 1;
  return `toast-${toastIdCounter}-${Date.now()}`;
}

/**
 * B-6 §I (M19): hard cap on simultaneously visible toasts — never exceeded.
 * Tunable; 4 keeps the verified ≥3-simultaneous-distinct flows intact while
 * bounding notification flooding.
 */
const MAX_VISIBLE_TOASTS = 4;

/**
 * Monotonic recency/refresh ordinal shared by every toast the app shows.
 * Doubles as the eviction "age" (lower = older) and as the timer-restart
 * nonce a coalesced duplicate bumps.
 */
let toastRecencyCounter = 0;

/**
 * Protected toasts resist eviction (B-6 §I): errors carry information the
 * operator must not lose to later chatter, and `duration: 0` is an explicit
 * sticky request (a forward guard — no production call site passes 0 today).
 */
function isProtectedToast(toast: ToastData): boolean {
  return toast.variant === 'error' || toast.duration === 0;
}

/**
 * Toast context value shape.
 */
export interface ToastContextValue {
  /** Show a success toast */
  showSuccess: (message: string, duration?: number) => void;
  /** Show an error toast */
  showError: (message: string, duration?: number) => void;
  /** Show a warning toast */
  showWarning: (message: string, duration?: number) => void;
  /** Show a toast with custom options */
  showToast: (options: { message: string; variant: ToastVariant; duration?: number }) => void;
  /** Dismiss a specific toast by ID */
  dismissToast: (id: string) => void;
  /** Dismiss all toasts */
  dismissAllToasts: () => void;
}

/**
 * Toast context - provides toast functionality to the app.
 */
export const ToastContext = createContext<ToastContextValue>({
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
  showToast: () => {},
  dismissToast: () => {},
  dismissAllToasts: () => {},
});

/**
 * Props for ToastProvider component.
 */
interface ToastProviderProps {
  children: ReactNode;
}

/**
 * ToastProvider component.
 *
 * Manages toast state and renders the ToastContainer.
 * Provides methods to show and dismiss toasts via context.
 *
 * @example
 * ```tsx
 * // In main.tsx
 * root.render(
 *   <ToastProvider>
 *     <SessionProvider>
 *       <App />
 *     </SessionProvider>
 *   </ToastProvider>
 * );
 * ```
 */
export function ToastProvider({ children }: ToastProviderProps): React.ReactElement {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  // B-6 §I / impl-review CL-1: a FIFO queue, not a scalar slot. Two addToast
  // calls in one React batch (e.g. the useRfi create flow) both append via
  // functional updates, so no event is lost to batching; ToastContainer drains
  // and acks one at a time so each gets its own SR announcement cycle.
  const [announcements, setAnnouncements] = useState<ToastAnnouncement[]>([]);

  /**
   * Add a new toast (B-6 §I: de-dupe + hard cap + announcement).
   *
   * - A toast identical in (variant, message) to a visible one COALESCES:
   *   the existing entry's refreshNonce bumps (its Toast restarts its timer
   *   on the same DOM node) and the incoming duration replaces the old one
   *   (protection recomputed — 0↔finite transitions included). Its recency
   *   updates for eviction ordering.
   * - A fresh toast beyond MAX_VISIBLE_TOASTS evicts the oldest UNPROTECTED
   *   visible toast; if every visible toast is protected, the oldest
   *   protected one goes — the cap is absolute (M19).
   * - EVERY call announces via the container's SR-only live region,
   *   coalesced duplicates included (parity with the pre-B-6 behavior where
   *   each duplicate mounted a fresh role="alert" node).
   */
  const addToast = useCallback(
    (message: string, variant: ToastVariant, duration?: number): void => {
      const id = generateToastId();
      toastRecencyCounter += 1;
      const nonce = toastRecencyCounter;
      // Enqueue the announcement (nonce doubles as its FIFO id). Functional
      // update → two calls in one batch BOTH append; neither is swallowed.
      setAnnouncements((prev) => [...prev, { id: nonce, text: message }]);
      setToasts((prev) => {
        const existing = prev.find(
          (toast) => toast.variant === variant && toast.message === message
        );
        if (existing) {
          return prev.map((toast) =>
            toast.id === existing.id ? { ...toast, duration, refreshNonce: nonce } : toast
          );
        }
        const incoming: ToastData = { id, message, variant, duration, refreshNonce: nonce };
        if (prev.length < MAX_VISIBLE_TOASTS) {
          return [...prev, incoming];
        }
        const unprotected = prev.filter((toast) => !isProtectedToast(toast));
        const evictionPool = unprotected.length > 0 ? unprotected : prev;
        const evicted = evictionPool.reduce((oldest, toast) =>
          (toast.refreshNonce ?? 0) < (oldest.refreshNonce ?? 0) ? toast : oldest
        );
        return [...prev.filter((toast) => toast.id !== evicted.id), incoming];
      });
    },
    []
  );

  /**
   * Dismiss a toast by ID.
   */
  const dismissToast = useCallback((id: string): void => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /** Ack the announcement the container just finished (queue advance). */
  const handleAnnounced = useCallback((id: number): void => {
    setAnnouncements((prev) => prev.filter((a) => a.id !== id));
  }, []);

  /**
   * Dismiss all toasts.
   */
  const dismissAllToasts = useCallback((): void => {
    setToasts([]);
  }, []);

  /**
   * Show a success toast.
   */
  const showSuccess = useCallback(
    (message: string, duration?: number): void => {
      addToast(message, 'success', duration);
    },
    [addToast]
  );

  /**
   * Show an error toast.
   */
  const showError = useCallback(
    (message: string, duration?: number): void => {
      // Error toasts last longer by default (8 seconds)
      addToast(message, 'error', duration ?? 8000);
    },
    [addToast]
  );

  /**
   * Show a warning toast.
   */
  const showWarning = useCallback(
    (message: string, duration?: number): void => {
      addToast(message, 'warning', duration);
    },
    [addToast]
  );

  /**
   * Show a toast with custom options.
   */
  const showToast = useCallback(
    (options: { message: string; variant: ToastVariant; duration?: number }): void => {
      addToast(options.message, options.variant, options.duration);
    },
    [addToast]
  );

  const contextValue = useMemo<ToastContextValue>(
    () => ({
      showSuccess,
      showError,
      showWarning,
      showToast,
      dismissToast,
      dismissAllToasts,
    }),
    [showSuccess, showError, showWarning, showToast, dismissToast, dismissAllToasts]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer
        toasts={toasts}
        onDismiss={dismissToast}
        announcements={announcements}
        onAnnounced={handleAnnounced}
      />
    </ToastContext.Provider>
  );
}
